/**
 * FIXTURES — variable creation.
 *
 * ============================================================================
 * EVERY BRAND, RIGHTS HOLDER, VENUE AND FIGURE IN THIS FILE IS INVENTED.
 * ============================================================================
 *
 * No client register, no real rights holder, no approved commercial rate. That is
 * not caution for its own sake: `docs/mmm/OPEN-ITEMS.md` §D lists the real register
 * and the real pricing among the things that must never travel, and a fixture is the
 * easiest place for real data to end up by accident. If you recognise a name here,
 * it is a coincidence worth reporting.
 *
 * The SHAPES are real — they are what `MmmPort` promises and what the API will have
 * to satisfy. Only the values are made up, and they are made up to exercise the
 * screens honestly: absences are present, one contract has no date, geography stops
 * at national outside the USA, and the metric libraries are deliberately uneven.
 */

import type {
  Id,
  MmmArchetype,
  MmmContractRecord,
  MmmCountry,
  MmmDqsAgent,
  MmmEntitlement,
  MmmGeoNode,
  MmmMetric,
  MmmRateCard,
  MmmRateCardRevision,
  MmmSpendTemplate,
  MmmVisibilityChannel,
} from '../../contracts';

/* ------------------------------------------------------------------ contracts -- */

/**
 * Twelve invented contracts across four archetypes.
 *
 * `state` and `date` are deliberately mixed: the register this stands in for holds
 * either a stage description or a date, and sometimes neither (NEW-33). A screen that
 * renders "Not stated" as a no is wrong, so the fixture makes that case reachable.
 */
export const VC_CONTRACTS: readonly MmmContractRecord[] = [
  {
    id: 'c-001',
    contract: {
      ref: 'NW-2401',
      name: 'Northwind × Riverside FC',
      state: 'Contracted',
      date: '2024-07-01',
    },
    masterbrand: 'Northwind',
    brandId: 'b-northwind',
    rightsHolder: 'Riverside FC',
    league: 'Premier Division',
    businessUnit: 'Retail Banking',
    market: 'United Kingdom',
    venue: 'Riverside Park',
    territory: 'UK & Ireland',
    archetype: 'Team',
  },
  {
    id: 'c-002',
    contract: {
      ref: 'NW-2402',
      name: 'Northwind × Coast City Arena',
      state: 'Contracted',
      date: '2023-09-15',
    },
    masterbrand: 'Northwind',
    brandId: 'b-northwind',
    rightsHolder: 'Coast City Arena',
    league: 'n/a',
    businessUnit: 'Retail Banking',
    market: 'United States',
    venue: 'Coast City Arena',
    territory: 'North America',
    archetype: 'Venue',
  },
  {
    id: 'c-003',
    contract: {
      ref: 'NW-2403',
      name: 'Northwind × Harbour Marathon',
      state: 'In renewal',
      date: null,
    },
    masterbrand: 'Northwind',
    brandId: 'b-northwind',
    rightsHolder: 'Harbour Marathon',
    league: 'n/a',
    businessUnit: 'Brand',
    market: 'United States',
    venue: 'Harbour Waterfront',
    territory: 'North America',
    archetype: 'Event',
  },
  {
    id: 'c-004',
    contract: {
      ref: 'NW-2404',
      name: 'Northwind × Lakeside Festival',
      state: 'Not stated',
      date: null,
    },
    masterbrand: 'Northwind',
    brandId: 'b-northwind',
    rightsHolder: 'Lakeside Festival',
    league: 'n/a',
    businessUnit: 'Brand',
    market: 'Germany',
    venue: 'Lakeside Meadows',
    territory: 'DACH',
    archetype: 'Festival',
  },
  {
    id: 'c-005',
    contract: {
      ref: 'HL-1187',
      name: 'Harbourline × Summit University',
      state: 'Contracted',
      date: '2025-01-10',
    },
    masterbrand: 'Harbourline',
    brandId: 'b-harbourline',
    rightsHolder: 'Summit University Athletics',
    league: 'Collegiate Conference North',
    businessUnit: 'Insurance',
    market: 'United States',
    venue: 'Summit Field',
    territory: 'North America',
    archetype: 'Collegiate',
  },
  {
    id: 'c-006',
    contract: {
      ref: 'HL-1188',
      name: 'Harbourline × Clearwater Trust',
      state: 'Contracted',
      date: '2024-03-01',
    },
    masterbrand: 'Harbourline',
    brandId: 'b-harbourline',
    rightsHolder: 'Clearwater Trust',
    league: 'n/a',
    businessUnit: 'Corporate Affairs',
    market: 'United Kingdom',
    venue: 'n/a',
    territory: 'UK & Ireland',
    archetype: 'Cause',
  },
  {
    id: 'c-007',
    contract: {
      ref: 'HL-1189',
      name: 'Harbourline × Meridian Rovers',
      state: 'Contracted',
      date: '2022-08-01',
    },
    masterbrand: 'Harbourline',
    brandId: 'b-harbourline',
    rightsHolder: 'Meridian Rovers',
    league: 'Premier Division',
    businessUnit: 'Insurance',
    market: 'United Kingdom',
    venue: 'Meridian Ground',
    territory: 'UK & Ireland',
    archetype: 'Team',
  },
  {
    id: 'c-008',
    contract: {
      ref: 'AC-0042',
      name: 'Alpenroute × Kessel Skiing',
      state: 'Contracted',
      date: '2024-11-20',
    },
    masterbrand: 'Alpenroute',
    brandId: 'b-alpenroute',
    rightsHolder: 'Kessel Skiing',
    league: 'Alpine Series',
    businessUnit: 'Mobility',
    market: 'Austria',
    venue: 'Kessel Valley',
    territory: 'DACH',
    archetype: 'Event',
  },
  {
    id: 'c-009',
    contract: {
      ref: 'AC-0043',
      name: 'Alpenroute × Nordbahn Stadium',
      state: 'Signed, start pending',
      date: null,
    },
    masterbrand: 'Alpenroute',
    brandId: 'b-alpenroute',
    rightsHolder: 'Nordbahn Stadium',
    league: 'n/a',
    businessUnit: 'Mobility',
    market: 'Germany',
    venue: 'Nordbahn Stadium',
    territory: 'DACH',
    archetype: 'Venue',
  },
  {
    id: 'c-010',
    contract: {
      ref: 'AC-0044',
      name: 'Alpenroute × Talwind Open',
      state: 'Contracted',
      date: '2023-05-05',
    },
    masterbrand: 'Alpenroute',
    brandId: 'b-alpenroute',
    rightsHolder: 'Talwind Open',
    league: 'n/a',
    businessUnit: 'Brand',
    market: 'Switzerland',
    venue: 'Talwind Courts',
    territory: 'DACH',
    archetype: 'Event',
  },
  {
    id: 'c-011',
    contract: {
      ref: 'NW-2405',
      name: 'Northwind × Eastgate Athletic',
      state: 'Contracted',
      date: '2025-06-01',
    },
    masterbrand: 'Northwind',
    brandId: 'b-northwind',
    rightsHolder: 'Eastgate Athletic',
    league: 'Championship Tier',
    businessUnit: 'Retail Banking',
    market: 'United States',
    venue: 'Eastgate Bowl',
    territory: 'North America',
    archetype: 'Team',
  },
  {
    id: 'c-012',
    contract: {
      ref: 'HL-1190',
      name: 'Harbourline × Portside Regatta',
      state: 'Expired',
      date: '2021-04-30',
    },
    masterbrand: 'Harbourline',
    brandId: 'b-harbourline',
    rightsHolder: 'Portside Regatta',
    league: 'n/a',
    businessUnit: 'Corporate Affairs',
    market: 'United Kingdom',
    venue: 'Portside Harbour',
    territory: 'UK & Ireland',
    archetype: 'Other',
  },
];

/* ------------------------------------------------------------------ geography -- */

/**
 * Ten countries; only the USA reaches DMA level, exactly as the platform does today
 * (NEW-13). Everything else resolves to national, and the screens say so rather than
 * pretending a finer selection exists.
 */
export const VC_COUNTRIES: readonly MmmCountry[] = [
  { code: 'US', name: 'United States', availableTo: 'dma' },
  { code: 'GB', name: 'United Kingdom', availableTo: 'national' },
  { code: 'DE', name: 'Germany', availableTo: 'national' },
  { code: 'AT', name: 'Austria', availableTo: 'national' },
  { code: 'CH', name: 'Switzerland', availableTo: 'national' },
  { code: 'FR', name: 'France', availableTo: 'national' },
  { code: 'ES', name: 'Spain', availableTo: 'national' },
  { code: 'IT', name: 'Italy', availableTo: 'national' },
  { code: 'NL', name: 'Netherlands', availableTo: 'national' },
  { code: 'IE', name: 'Ireland', availableTo: 'national' },
];

/** A flat node list. The tree is walked with `parentId`, never shipped whole. */
export const VC_GEO_NODES: readonly MmmGeoNode[] = [
  { id: 'us-ne', name: 'Northeast', level: 'region', parentId: 'US' },
  { id: 'us-mw', name: 'Midwest', level: 'region', parentId: 'US' },
  { id: 'us-s', name: 'South', level: 'region', parentId: 'US' },
  { id: 'us-w', name: 'West', level: 'region', parentId: 'US' },

  { id: 'dma-501', name: 'Metro North', level: 'dma', parentId: 'us-ne' },
  { id: 'dma-504', name: 'Bay Harbour', level: 'dma', parentId: 'us-ne' },
  { id: 'dma-506', name: 'Granite Falls', level: 'dma', parentId: 'us-ne' },
  { id: 'dma-602', name: 'Lakeshore', level: 'dma', parentId: 'us-mw' },
  { id: 'dma-618', name: 'Prairie Center', level: 'dma', parentId: 'us-mw' },
  { id: 'dma-623', name: 'Ironworks', level: 'dma', parentId: 'us-mw' },
  { id: 'dma-701', name: 'Gulf Reach', level: 'dma', parentId: 'us-s' },
  { id: 'dma-710', name: 'Pinebelt', level: 'dma', parentId: 'us-s' },
  { id: 'dma-722', name: 'Delta Crossing', level: 'dma', parentId: 'us-s' },
  { id: 'dma-803', name: 'Coast City', level: 'dma', parentId: 'us-w' },
  { id: 'dma-819', name: 'High Desert', level: 'dma', parentId: 'us-w' },
  { id: 'dma-825', name: 'Cascade', level: 'dma', parentId: 'us-w' },

  { id: 'city-coast', name: 'Coast City', level: 'city', parentId: 'dma-803' },
  { id: 'city-eastgate', name: 'Eastgate', level: 'city', parentId: 'dma-602' },
];

/* ------------------------------------------------------- channels and metrics -- */

export const VC_CHANNELS: readonly MmmVisibilityChannel[] = [
  { id: 'ch-perimeter', name: 'Perimeter LED', metric: 'Quality Impressions', family: 'In-venue' },
  { id: 'ch-jersey', name: 'Shirt front', metric: 'Quality Impressions', family: 'In-venue' },
  {
    id: 'ch-backdrop',
    name: 'Interview backdrop',
    metric: 'Quality Impressions',
    family: 'In-venue',
  },
  { id: 'ch-bigscreen', name: 'Big screen', metric: 'Quality Impressions', family: 'In-venue' },
  { id: 'ch-naming', name: 'Venue naming', metric: 'Mentions', family: 'In-venue' },
  {
    id: 'ch-broadcast',
    name: 'Broadcast on-screen',
    metric: 'Quality Impressions',
    family: 'Broadcast',
  },
  { id: 'ch-highlights', name: 'Highlights packages', metric: 'Views', family: 'Broadcast' },
  { id: 'ch-owned-social', name: 'Owned social', metric: 'Impressions', family: 'Digital' },
  { id: 'ch-partner-social', name: 'Partner social', metric: 'Impressions', family: 'Digital' },
  { id: 'ch-creator', name: 'Creator content', metric: 'Views', family: 'Digital' },
  { id: 'ch-website', name: 'Partner website', metric: 'Visits', family: 'Digital' },
  { id: 'ch-newsletter', name: 'Partner newsletter', metric: 'Opens', family: 'Digital' },
  { id: 'ch-hospitality', name: 'Hospitality', metric: 'Attendees', family: 'Experiential' },
  { id: 'ch-fanzone', name: 'Fan zone activation', metric: 'Attendees', family: 'Experiential' },
];

/**
 * The library is deliberately uneven.
 *
 * Metric names are spreadsheet strings, not KPI-library ids, and the sheet's own
 * category lists are incomplete (NEW-12). The fixture keeps that so the screens are
 * built against the mess they will actually meet, not a tidied version of it.
 */
export const VC_METRICS: readonly MmmMetric[] = [
  { id: 'm-impressions', name: 'Impressions', category: 'reach', unit: 'count' },
  { id: 'm-quality-impressions', name: 'Quality Impressions', category: 'strength', unit: 'count' },
  { id: 'm-views', name: 'Views', category: 'reach', unit: 'count' },
  { id: 'm-visits', name: 'Visits', category: 'reach', unit: 'count' },
  { id: 'm-opens', name: 'Opens', category: 'reach', unit: 'count' },
  { id: 'm-attendees', name: 'Attendees', category: 'reach', unit: 'count' },
  { id: 'm-mentions', name: 'Mentions', category: 'reach', unit: 'count' },
  { id: 'm-unique-reach', name: 'Unique reach', category: 'reach', unit: 'count' },
  { id: 'm-dwell', name: 'Dwell time', category: 'strength', unit: 'seconds' },
  { id: 'm-share-of-voice', name: 'Share of voice', category: 'strength', unit: 'percent' },
  { id: 'm-prominence', name: 'Prominence index', category: 'strength', unit: 'index' },
  { id: 'm-clarity', name: 'Clarity score', category: 'strength', unit: 'index' },
  { id: 'm-dqs', name: 'Data quality score', category: 'quality', unit: 'index' },
  { id: 'm-coverage', name: 'Source coverage', category: 'quality', unit: 'percent' },
  { id: 'm-vintage', name: 'Data vintage', category: 'quality', unit: 'days' },
  { id: 'm-smv', name: 'Sponsorship media value', category: 'smv', unit: 'currency' },
  { id: 'm-cpm', name: 'Effective CPM', category: 'smv', unit: 'currency' },
  { id: 'm-rate', name: 'Applied rate', category: 'smv', unit: 'currency' },
];

/**
 * Entitlements, each resolving to the channel it produces exposure on.
 *
 * `defaults` is a SUGGESTION per category, never a lock — the proposal is put to the
 * user and confirmed, which is the whole point of the entitlement pull (NEW-53).
 */
export const VC_ENTITLEMENTS: readonly MmmEntitlement[] = [
  {
    id: 'e-shirt-front',
    name: 'Shirt front branding',
    channel: 'ch-jersey',
    defaults: {
      reach: { primary: 'm-impressions', secondary: ['m-unique-reach'] },
      strength: { primary: 'm-quality-impressions', secondary: ['m-prominence', 'm-clarity'] },
      smv: { primary: 'm-smv', secondary: ['m-cpm'] },
    },
  },
  {
    id: 'e-perimeter',
    name: 'Perimeter boards',
    channel: 'ch-perimeter',
    defaults: {
      reach: { primary: 'm-impressions', secondary: [] },
      strength: { primary: 'm-quality-impressions', secondary: ['m-dwell'] },
    },
  },
  {
    id: 'e-backdrop',
    name: 'Press conference backdrop',
    channel: 'ch-backdrop',
    defaults: { strength: { primary: 'm-quality-impressions', secondary: ['m-clarity'] } },
  },
  {
    id: 'e-bigscreen',
    name: 'Big screen slot',
    channel: 'ch-bigscreen',
    defaults: { reach: { primary: 'm-attendees', secondary: [] } },
  },
  {
    id: 'e-naming',
    name: 'Venue naming rights',
    channel: 'ch-naming',
    defaults: {
      reach: { primary: 'm-mentions', secondary: ['m-unique-reach'] },
      strength: { primary: 'm-share-of-voice', secondary: [] },
      smv: { primary: 'm-smv', secondary: [] },
    },
  },
  {
    id: 'e-broadcast-billboard',
    name: 'Broadcast billboard',
    channel: 'ch-broadcast',
    defaults: { strength: { primary: 'm-quality-impressions', secondary: ['m-prominence'] } },
  },
  {
    id: 'e-highlights',
    name: 'Highlights sponsorship',
    channel: 'ch-highlights',
    defaults: { reach: { primary: 'm-views', secondary: [] } },
  },
  {
    id: 'e-social-posts',
    name: 'Partner social posts',
    channel: 'ch-partner-social',
    defaults: { reach: { primary: 'm-impressions', secondary: ['m-unique-reach'] } },
  },
  {
    id: 'e-owned-social',
    name: 'Owned channel content',
    channel: 'ch-owned-social',
    defaults: { reach: { primary: 'm-impressions', secondary: [] } },
  },
  {
    id: 'e-creator',
    name: 'Creator collaboration',
    channel: 'ch-creator',
    defaults: { reach: { primary: 'm-views', secondary: [] } },
  },
  {
    id: 'e-web-presence',
    name: 'Website presence',
    channel: 'ch-website',
    defaults: { reach: { primary: 'm-visits', secondary: [] } },
  },
  {
    id: 'e-newsletter',
    name: 'Newsletter placement',
    channel: 'ch-newsletter',
    defaults: { reach: { primary: 'm-opens', secondary: [] } },
  },
  {
    id: 'e-hospitality',
    name: 'Hospitality allocation',
    channel: 'ch-hospitality',
    defaults: { reach: { primary: 'm-attendees', secondary: [] } },
  },
  {
    id: 'e-fanzone',
    name: 'Fan zone presence',
    channel: 'ch-fanzone',
    defaults: { reach: { primary: 'm-attendees', secondary: ['m-dwell'] } },
  },
];

/* ------------------------------------------------------------------ rate card -- */

/**
 * PLACEHOLDER FIGURES. Round numbers, chosen to be obviously invented.
 *
 * The approved commercial rates are internal pricing and are on the §D list. Nothing
 * here is a quote, and the screen that renders it says so on its face (NEW-29).
 */
const VC_RATE_CARD: MmmRateCard = {
  currency: 'EUR',
  perMetric: 500,
  baseFee: 20_000,
  perMarket: 2_000,
  perPartnership: 1_000,
  refreshDiscount: 0.3,
  volumeThreshold: 25,
  volumeDiscount: 0.1,
  baseDays: 20,
  // The three data-quality service fees are PO-APPROVED exact figures (NEW-40, NEW-84);
  // everything else on this card is invented. The engine's copies of the same three
  // numbers are the ones slated for removal (OPEN-BUILDER-16) — this read is the source.
  agents: {
    dqs_assessment: { fee: 1_200, days: 5 },
    data_recommendation: { fee: 1_800, days: 3 },
    data_qa: { fee: 2_900, days: 10 },
  },
  // PO-approved with the three above (DEC-28). Runs alongside the build, not after it.
  customTaxonomy: { fee: 2_900, days: 10 },
};

/** The version a write has to present. Fixed, because nothing here persists. */
export const VC_RATE_CARD_VERSION = 'fixture-1';

export const VC_RATE_CARD_REVISION: MmmRateCardRevision = {
  card: VC_RATE_CARD,
  version: VC_RATE_CARD_VERSION,
  updatedAt: '2026-01-05',
};

/* --------------------------------------------------- entitlement proposals -- */

/**
 * Which visibility channels a contract archetype can produce exposure on.
 *
 * ⚠️ STAND-IN with the right SHAPE. The real proposal comes from the entitlement
 * register — the rights each contract actually grants, resolved to the channel each
 * right produces (integration point 5, NEW-53). Until that pull exists the proposal
 * derives from the archetype, which is the coarsest true statement available: what a
 * deal IS bounds which entitlements can exist against it (NEW-34).
 *
 * One contract reaches MANY channels. A proposal that returned one would be wrong.
 */
export const VC_ARCHETYPE_CHANNELS: Readonly<Record<MmmArchetype, readonly Id[]>> = {
  Team: [
    'ch-jersey',
    'ch-perimeter',
    'ch-broadcast',
    'ch-highlights',
    'ch-partner-social',
    'ch-hospitality',
  ],
  Venue: ['ch-naming', 'ch-perimeter', 'ch-bigscreen', 'ch-hospitality', 'ch-fanzone'],
  Event: ['ch-perimeter', 'ch-backdrop', 'ch-broadcast', 'ch-partner-social', 'ch-fanzone'],
  Festival: ['ch-bigscreen', 'ch-partner-social', 'ch-creator', 'ch-fanzone', 'ch-hospitality'],
  Collegiate: ['ch-jersey', 'ch-perimeter', 'ch-broadcast', 'ch-owned-social', 'ch-hospitality'],
  Cause: ['ch-owned-social', 'ch-partner-social', 'ch-website', 'ch-newsletter'],
  Other: ['ch-partner-social', 'ch-website'],
};

/* ------------------------------------------------------------ spend template -- */

/**
 * The spend template (DEC-12). Its shape is owned here, not by whoever fills it, and the
 * basis question is asked in words beside the download rather than buried in a column.
 */
export const VC_SPEND_TEMPLATE: MmmSpendTemplate = {
  filename: 'PNET_MMM_Spend_Template.csv',
  columns: [
    { key: 'contract_id', label: 'Contract id', required: true, example: 'c-001' },
    { key: 'entitlement_id', label: 'Entitlement id', required: true, example: 'e-shirt-front' },
    { key: 'start_date', label: 'Start date (YYYY-MM-DD)', required: true, example: '2024-01-01' },
    { key: 'end_date', label: 'End date (YYYY-MM-DD)', required: true, example: '2024-12-31' },
    { key: 'amount', label: 'Amount', required: true, example: '125000' },
    { key: 'currency', label: 'Currency (ISO 4217)', required: false, example: 'EUR' },
  ],
  notes: [
    'One row per contract × entitlement × period. A contract with no spend against an entitlement has NO row — never a zero row.',
    'Declare what the figures include before uploading: activation only, activation plus fee, or fully loaded. The declaration lands on every row and is what makes two engagements comparable.',
    'Amounts are whole currency units with no thousands separator. Dates are ISO (YYYY-MM-DD).',
  ],
};

/* ----------------------------------------------------------- DQS assessments -- */

/**
 * The data-quality assessments on file, keyed by the MMM run they were produced against.
 *
 * Deliberately covers `RUN-001` only: the default run demonstrates auto-connect and any
 * other identifier demonstrates the in-scope-but-absent path. Both have to be reachable
 * (NEW-28). Scores and dates are invented.
 */
export const VC_DQS_ASSESSMENTS: readonly {
  readonly id: string;
  readonly agent: MmmDqsAgent;
  readonly runId: string;
  readonly ranAt: string;
  readonly score: number;
  readonly completeness: number;
  readonly note: string;
}[] = [
  {
    id: 'DCS-A-0141',
    agent: 'dqs_assessment',
    runId: 'RUN-001',
    ranAt: '2026-08-11',
    score: 84,
    completeness: 1,
    note: 'Scorecard complete across every source in the model.',
  },
  {
    id: 'DCS-Q-0139',
    agent: 'data_qa',
    runId: 'RUN-001',
    ranAt: '2026-08-09',
    score: 72,
    completeness: 0.5,
    note: 'QA covered half of the portfolio in this run.',
  },
];
